// Shop-unlock-by-defeat tracking. A snordeluxe variety is "unlocked" for
// shop purchase only after the player defeats one in battle. Because the
// snordeluxe-server backend strips unknown user fields (same problem the
// stat overrides hit), the unlock list is cached client-side in localStorage
// keyed by user.id and re-attached on every session start.
//
// Public API:
//   markSnordeluxeDefeated(name)  — call when the local player kills an enemy
//   hasDefeated(user, name)       — true if the variety is unlocked
//   loadDefeatedFor(userId)       — read the localStorage cache
//   saveDefeatedFor(user)         — write the in-memory list back to LS
//   applyDefeatedToUser(user)     — read LS and copy onto user.defeatedSnordeluxes

import { getUser } from './views.js';
import { snordeluxeAssets } from '../snordeluxeAssets.js';

const LS_PREFIX = 'snordeluxe-defeated-';

function evolutionFamilyFor(snordeluxeName) {
    if (!snordeluxeName || !snordeluxeAssets[snordeluxeName]) return snordeluxeName ? [snordeluxeName] : [];

    let root = snordeluxeName;
    let changed = true;
    while (changed) {
        changed = false;
        for (const [name, data] of Object.entries(snordeluxeAssets)) {
            if (data && data.evolution === root) {
                root = name;
                changed = true;
                break;
            }
        }
    }

    const family = [];
    let current = root;
    const seen = new Set();
    while (current && snordeluxeAssets[current] && !seen.has(current)) {
        seen.add(current);
        family.push(current);
        current = snordeluxeAssets[current].evolution;
    }
    return family;
}

export function loadDefeatedFor(userId) {
    if (!userId || typeof localStorage === 'undefined') return [];
    try {
        const raw = localStorage.getItem(LS_PREFIX + userId);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : [];
    } catch (_) { return []; }
}

export function saveDefeatedFor(user) {
    if (!user || !user.id || typeof localStorage === 'undefined') return;
    const list = Array.isArray(user.defeatedSnordeluxes) ? user.defeatedSnordeluxes : [];
    try {
        localStorage.setItem(LS_PREFIX + user.id, JSON.stringify(list));
    } catch (_) {}
}

export function applyDefeatedToUser(user) {
    if (!user || !user.id) return user;
    const list = loadDefeatedFor(user.id);
    user.defeatedSnordeluxes = list;
    return user;
}

export function hasDefeated(user, snordeluxeName) {
    if (!user || !snordeluxeName) return false;
    const list = Array.isArray(user.defeatedSnordeluxes) ? user.defeatedSnordeluxes : [];
    const family = evolutionFamilyFor(snordeluxeName);
    return family.some(name => list.includes(name));
}

// Adds `name` to the live user's defeatedSnordeluxes (de-duped) and
// persists to localStorage. Called from handleCharacterDied_Manager
// when the local player gets the kill.
export function markSnordeluxeDefeated(name) {
    if (!name) return;
    const user = getUser();
    if (!user) return;
    if (!Array.isArray(user.defeatedSnordeluxes)) user.defeatedSnordeluxes = [];
    const family = evolutionFamilyFor(name);
    const baseName = family[0] || name;
    const unlockNames = Array.from(new Set([name, baseName]));
    let changed = false;
    unlockNames.forEach(unlockName => {
        if (!user.defeatedSnordeluxes.includes(unlockName)) {
            user.defeatedSnordeluxes.push(unlockName);
            changed = true;
        }
    });
    if (!changed) return;
    saveDefeatedFor(user);
    console.log(`[defeatedSnordeluxes] Unlocked '${unlockNames.join(', ')}' for shop purchase.`);
}
